/* eslint-disable jsx-a11y/media-has-caption */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import Layout from "./Layout";
import useVideoSystem from "./useVideoSystem";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PLATFORM = "Video";

/** Player for an HTML video. */
const Video = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    src,
    playerLoadErrorMessage = "The video could not load.",
    onEnded: onEndedProp,
    onError: onErrorProp,
    onLoadedMetadata: onLoadedMetadataProp,
    onPause: onPauseProp,
    onPlay: onPlayProp,
    onPlaying: onPlayingProp,
    onTimeUpdate: onTimeUpdateProp,
    autoPlay,
    muted,
    poster: posterProp,
    preload = "metadata",
    className,
    style,
    children,
    playLabel = "Play",
    forceLibraryLoad,
    hasParentPoster,
    ...rest
  } = _ref;
  const platformPlayerRef = useRef();
  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    platformPlayerRef.current.load();
  }, [children, platformPlayerRef]);

  // Setup the video system
  const {
    elementRef,
    showLoader,
    apiLoadingError,
    poster,
    imperativeAPI,
    eventHandlers,
    platformPlayerIsReady,
    videoSupportReady,
    videoAutoPlayFromDelayedInjection
  } = useVideoSystem({
    platform: PLATFORM,
    poster: posterProp,
    onEnded: onEndedProp,
    onError: onErrorProp,
    onLoadedMetadata: onLoadedMetadataProp,
    onPause: onPauseProp,
    onPlay: onPlayProp,
    onPlaying: onPlayingProp,
    onTimeUpdate: onTimeUpdateProp,
    forceLibraryLoad: forceLibraryLoad,
    getEventDetail: useCallback(() => platformPlayerRef.current ? {
      mediaId: platformPlayerRef.current.currentSrc,
      currentTime: platformPlayerRef.current.currentTime,
      duration: platformPlayerRef.current.duration
    } : {}, [platformPlayerRef]),
    play: useCallback(() => platformPlayerRef.current.play(), [platformPlayerRef]),
    pause: useCallback(() => platformPlayerRef.current.pause(), [platformPlayerRef])
  });

  // Expose imperative control to a given `ref`
  useImperativeHandle(ref, () => imperativeAPI);
  useEffect(() => {
    platformPlayerIsReady();
  }, [platformPlayerIsReady]);
  return /*#__PURE__*/_jsx(Layout, {
    loading: showLoader,
    error: apiLoadingError ? playerLoadErrorMessage : undefined,
    poster: poster,
    play: imperativeAPI.play,
    ref: elementRef,
    className: className,
    style: style,
    playLabel: playLabel,
    hasParentPoster: hasParentPoster,
    children: videoSupportReady ? /*#__PURE__*/_jsx("video", {
      ...rest,
      controls: true,
      ref: platformPlayerRef,
      src: src,
      autoPlay: videoAutoPlayFromDelayedInjection || autoPlay,
      muted: muted,
      preload: preload,
      ...eventHandlers,
      children: children
    }) : /*#__PURE__*/_jsx(_Fragment, {})
  });
});
Video.displayName = "Video";
export default Video;
Video.propTypes = {
  /** `<source>` element(s) */
  children: PropTypes.node,
  /** The URL of the video source. */
  src: PropTypes.string,
  /** The message to display if a player is not able to initially load. Additional playback errors will be handled by the platform's player. */
  playerLoadErrorMessage: PropTypes.node,
  /** Callback to execute when playback or streaming has stopped because the end of the media was reached or because no further data is available. */
  onEnded: PropTypes.func,
  /** Callback to execute when there is an error event. */
  onError: PropTypes.func,
  /** Callback to execute when video metadata has loaded. */
  onLoadedMetadata: PropTypes.func,
  /** Callback to execute when video is paused. */
  onPause: PropTypes.func,
  /** Callback to execute when video is no longer paused. */
  onPlay: PropTypes.func,
  /** Callback to execute after playback is first started, and whenever it is restarted. */
  onPlaying: PropTypes.func,
  /** Callback to execute when video's current position has changed (i.e., during playback). */
  onTimeUpdate: PropTypes.func,
  /** Whether the video autoplays. See the Details section for more information. */
  autoPlay: PropTypes.bool,
  /** Whether the video is muted. */
  muted: PropTypes.bool,
  /** Poster image to display before playing. Can be an element or URL string. */
  poster: PropTypes.node,
  /** A hint to the browser about what will lead to the best user experience with regards to what content is loaded before the video is played. See the HTML `<video>` element's `preload` documentation for more information. */
  preload: PropTypes.oneOf(["none", "metadata", "auto"]),
  /** Play button label */
  playLabel: PropTypes.string
};